/**
 * CustomerDashboard — landing page after login.
 *
 * All three side panels are wired against real backends:
 *
 *   - Wallet card                → GET /v1/wallet/wallets-mine
 *   - Active shipments preview   → GET /v1/mail-items/me?role=all&pageSize=3
 *   - Recent notifications       → listMyNotifications (frontend synthesis,
 *                                  see lib/api/notifications.ts)
 *
 * The customer-profile card (Account panel) keeps its existing
 * `GET /v1/customers/:id` wiring.
 */
import { useMemo, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Bell,
  CreditCard,
  Loader2,
  MessageSquare,
  Package,
  Search,
  ShieldCheck,
  Truck,
  Wallet as WalletIcon,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import { listMyMailItems, type MailItemSummary } from '../../lib/api/mail-items';
import {
  listMyNotifications,
  type NotificationItem,
} from '../../lib/api/notifications';
import { getOwnWallet, type WalletResponse } from '../../lib/api/wallet';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { formatNaira } from '../nipost-pay/NipostPayUI';
import { StatusBadge } from './PortalUI';
import { useCustomerProfile } from './useCustomerProfile';

interface QuickAction {
  readonly label: string;
  readonly icon: typeof Package;
  readonly desc: string;
  readonly link: string;
  readonly color: string;
  readonly enabled: boolean;
}

const QUICK_ACTIONS: readonly QuickAction[] = [
  { label: 'Track a Package', icon: Search,         desc: 'Look up by tracking number',     link: '/portal/tracking', color: 'bg-info',          enabled: true  },
  { label: 'My Shipments',    icon: Package,        desc: 'View parcels you sent / received', link: '/portal/shipments', color: 'bg-nipost-green',  enabled: true  },
  { label: 'My Complaints',   icon: MessageSquare,  desc: 'Track your filed complaints',    link: '/portal/complaints', color: 'bg-danger',        enabled: true  },
  { label: 'Notifications',   icon: Bell,           desc: 'Inbox of recent updates',        link: '/portal/notifications', color: 'bg-nipost-gold',  enabled: true  },
  { label: 'Profile & KYC',   icon: ShieldCheck,    desc: 'Manage your account',            link: '/portal/profile',  color: 'bg-warning',       enabled: true  },
];

const STATUS_COLOR: Record<string, string> = {
  PENDING_DROPOFF: 'bg-amber-100 text-amber-800 border-amber-200',
  IN_TRANSIT: 'bg-blue-100 text-blue-800 border-blue-200',
  OUT_FOR_DELIVERY: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  DELIVERED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  RETURNED: 'bg-gray-100 text-gray-700 border-gray-200',
  ON_HOLD: 'bg-orange-100 text-orange-800 border-orange-200',
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

function notificationIcon(n: NotificationItem): JSX.Element {
  if (n.kind === 'wallet') return <CreditCard className="w-3.5 h-3.5 text-gray-600" />;
  if (n.kind === 'kyc') return <ShieldCheck className="w-3.5 h-3.5 text-gray-600" />;
  if (n.kind === 'milestone') return <Truck className="w-3.5 h-3.5 text-gray-600" />;
  return <Bell className="w-3.5 h-3.5 text-gray-600" />;
}

// ---------------------------------------------------------------------------
// Wallet panel
// ---------------------------------------------------------------------------

function WalletPanel(): JSX.Element {
  const { token } = useCustomerAuth();

  const query = useQuery<WalletResponse, ApiError | Error>({
    queryKey: ['wallet', 'me'],
    queryFn: () => getOwnWallet({ bearerToken: token ?? undefined }),
    retry: (failureCount, err) => {
      // Don't retry 404 (wallet not yet onboarded) or 401/403.
      if (err instanceof ApiError && [401, 403, 404].includes(err.status)) return false;
      return failureCount < 1;
    },
    staleTime: 30_000,
    enabled: token !== null,
  });

  const isMissing =
    query.isError && query.error instanceof ApiError && query.error.status === 404;

  if (query.isLoading) {
    return (
      <div className="bg-white rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Wallet</h3>
        <div className="space-y-2 animate-pulse">
          <div className="h-7 bg-gray-100 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
      </div>
    );
  }

  if (isMissing) {
    return (
      <div className="bg-white rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Wallet</h3>
        <div className="text-center py-3">
          <WalletIcon className="w-8 h-8 text-muted mx-auto mb-2" />
          <p className="text-xs text-muted mb-3">You haven&apos;t set up a NIPOST wallet yet.</p>
          <Link
            to="/nipost-pay/onboarding"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            Activate your wallet <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="bg-white rounded-xl border border-border p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Wallet</h3>
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          <p className="font-semibold">Couldn&apos;t load wallet</p>
          <p className="mt-0.5">{query.error.message}</p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="mt-2 px-2 py-1 bg-white border border-red-200 text-red-800 rounded font-semibold"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const wallet = query.data;
  const balance = wallet?.balanceCached ?? wallet?.balance ?? '0';
  const tier = wallet?.kycTier ?? wallet?.tier ?? 'TIER_0';
  const walletNumber = wallet?.walletNumber ?? '—';

  return (
    <div className="bg-white rounded-xl border border-border p-5">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Wallet</h3>
        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-nipost-green/10 text-nipost-green rounded-full">
          {String(tier).replace('TIER_', 'Tier ')}
        </span>
      </div>
      <p className="text-2xl font-extrabold text-gray-900 leading-none">{formatNaira(balance)}</p>
      <p className="text-[11px] text-muted font-mono mt-1">{walletNumber}</p>
      <div className="mt-4 grid grid-cols-3 gap-1.5">
        <Link
          to="/portal/topup"
          className="text-[11px] font-semibold text-center px-2 py-1.5 bg-nipost-green text-white rounded-lg hover:bg-nipost-green-dark"
        >
          Top up
        </Link>
        <Link
          to="/nipost-pay/transfer"
          className="text-[11px] font-semibold text-center px-2 py-1.5 bg-white border border-border text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Send
        </Link>
        <Link
          to="/nipost-pay"
          className="text-[11px] font-semibold text-center px-2 py-1.5 bg-white border border-border text-gray-700 rounded-lg hover:bg-gray-50"
        >
          History
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent notifications panel
// ---------------------------------------------------------------------------

function NotificationsPanel(): JSX.Element {
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.customerId ?? '';
  const query = useQuery({
    queryKey: ['notifications', 'me', 'dashboard', customerId],
    queryFn: () =>
      listMyNotifications({
        pageSize: 5,
        bearerToken: token ?? undefined,
        customerId,
      }),
    staleTime: 30_000,
    enabled: customerId.length > 0,
  });

  const items = query.data?.items ?? [];
  const top = useMemo(() => {
    const unread = items.filter((n) => !n.read);
    return (unread.length > 0 ? unread : items).slice(0, 3);
  }, [items]);

  return (
    <div className="bg-white rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Recent notifications</h3>
        <Link
          to="/portal/notifications"
          className="text-[11px] font-semibold text-nipost-green hover:underline inline-flex items-center gap-0.5"
        >
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {query.isLoading ? (
        <p className="text-xs text-muted">
          <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…
        </p>
      ) : query.isError ? (
        <p className="text-xs text-red-700">Couldn&apos;t load notifications.</p>
      ) : top.length === 0 ? (
        <p className="text-xs text-muted">No notifications yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {top.map((n) => (
            <li key={n.id}>
              <Link to={n.deepLink} className="flex items-start gap-2 group">
                <span className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  {notificationIcon(n)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-xs leading-snug ${!n.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'} group-hover:text-nipost-green truncate`}>
                    {n.title}
                  </span>
                  <span className="block text-[10px] text-muted">{relativeTime(n.createdAt)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active shipments preview panel
// ---------------------------------------------------------------------------

function ShipmentRow({ item }: { item: MailItemSummary }): JSX.Element {
  const colour = STATUS_COLOR[item.status] ?? 'bg-gray-100 text-gray-700 border-gray-200';
  const roleTag = item.role === 'SENDER' ? 'Sent' : 'Received';
  const sender = item.senderName ?? '—';
  const receiver = item.receiverName ?? '—';
  return (
    <div className="px-4 py-3 border-b border-border last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/portal/tracking?tn=${encodeURIComponent(item.trackingNumber)}`}
            className="font-mono text-xs font-bold text-nipost-green hover:underline"
          >
            {item.trackingNumber}
          </Link>
          <p className="text-xs text-gray-700 mt-0.5 truncate">
            {sender} <span className="text-muted">→</span> {receiver}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${colour}`}>
            {item.status.replace(/_/g, ' ')}
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">{roleTag}</span>
        </div>
      </div>
    </div>
  );
}

function ShipmentsPanel(): JSX.Element {
  const { token } = useCustomerAuth();
  const query = useQuery({
    queryKey: ['mail-items', 'me', 'dashboard'],
    queryFn: () =>
      listMyMailItems(
        { pageSize: 3, role: 'all' },
        { bearerToken: token ?? undefined },
      ),
    staleTime: 30_000,
    enabled: token !== null,
  });

  const items = query.data?.items ?? [];

  return (
    <div className="lg:col-span-2 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Active Shipments</h2>
        {items.length > 0 ? (
          <Link to="/portal/shipments" className="text-sm text-nipost-green hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : null}
      </div>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {query.isLoading ? (
          <div className="p-5">
            <p className="text-xs text-muted">
              <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading shipments…
            </p>
          </div>
        ) : query.isError ? (
          <div className="p-5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900 m-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Couldn&apos;t load shipments</p>
                <p className="mt-0.5">{query.error.message}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-2 px-2 py-1 bg-white border border-red-200 text-red-800 rounded font-semibold"
            >
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <Package className="w-10 h-10 text-muted mx-auto mb-2" />
            <p className="text-sm font-semibold text-gray-700">No active shipments</p>
            <p className="text-xs text-muted mt-0.5 mb-3">
              Book your first parcel and track it from this dashboard.
            </p>
            <Link
              to="/portal/parcel-booking"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark"
            >
              Book a parcel <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          items.map((item) => <ShipmentRow key={item.id} item={item} />)
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CustomerDashboard(): JSX.Element {
  const { claims } = useCustomerAuth();
  const profile = useCustomerProfile();

  const fullName = profile.data?.fullName ?? claims?.displayName ?? '';
  const firstName = (fullName.split(/\s+/)[0] ?? '').trim() || 'there';
  const customerNumber = profile.data?.customerNumber ?? '—';
  const kycLevel = profile.data?.kycLevel ?? 'NONE';
  const kycLabel = kycLevel === 'NONE' ? 'PENDING' : kycLevel;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {firstName}</h1>
        <p className="text-sm text-muted mt-1">
          Here&apos;s an overview of your postal services account.
        </p>
      </div>

      {profile.isError ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          We couldn&apos;t load your profile. The customer service responded:{' '}
          <span className="font-mono">{profile.error.message}</span>. The dev token may not have
          permission to read this customer (see infrastructure/start/local.md §6).
        </div>
      ) : null}

      {(kycLevel === 'NONE' || kycLevel === 'BASIC') && profile.isSuccess ? (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-amber-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">Upgrade your KYC tier</p>
              <p className="text-[11px] text-amber-800/80">
                You&apos;re on {kycLabel}. Upgrade to STANDARD with your NIN to lift transaction limits.
              </p>
            </div>
          </div>
          <Link
            to="/portal/profile"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors flex-shrink-0"
          >
            Upgrade now <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {QUICK_ACTIONS.map((action) => (
          <Link
            key={action.label}
            to={action.link}
            className="bg-white rounded-xl border border-border p-4 hover:shadow-md transition-shadow group"
          >
            <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-3`}>
              <action.icon className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-sm text-gray-900">{action.label}</p>
            <p className="text-xs text-muted mt-0.5">{action.desc}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ShipmentsPanel />

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Account</h3>
            {profile.isLoading ? (
              <p className="text-xs text-muted">Loading…</p>
            ) : profile.data ? (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-muted">Customer number</dt>
                  <dd className="font-mono text-xs text-gray-900">{customerNumber}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-muted">Grade</dt>
                  <dd>
                    <StatusBadge status={profile.data.grade} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-muted">KYC tier</dt>
                  <dd className="text-xs font-semibold text-gray-900">{kycLabel}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-xs text-muted">No profile data.</p>
            )}
          </div>

          <WalletPanel />

          <NotificationsPanel />
        </div>
      </div>

      <div className="mt-8 text-xs text-muted">
        Need help?{' '}
        <Link to="/help" className="text-nipost-green hover:underline inline-flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Contact customer care
        </Link>
        <span className="mx-2">·</span>
        <Link to="/nipost-pay" className="text-nipost-green hover:underline inline-flex items-center gap-1">
          <CreditCard className="w-3 h-3" /> Payment options
        </Link>
      </div>
    </div>
  );
}
